import {
  AnalyticsLine,
  BlockQuoteLine,
  BookmarkLine,
  BriefcaseLine,
  CodeLine,
  CommentReadLine,
  CompassLine,
  ConversationLine,
  EditLine,
  EmojiSmileLine,
  FingerprintLine,
  HeartLine,
  HelpLine,
  LegalLine,
  LightbulbLine,
  LinkLine,
  LocationLine,
  MetalLine,
  MetricsLine,
  PaperDocLine,
  PuzzlePieceLine,
  RocketLine,
  ScribbleLine,
  StampLine,
  SummarizeLine,
  TargetLine,
  TaskQueueLine,
  ToDoListLine,
  UnknownLine,
} from "@dropbox/dig-icons/assets";
import { indexFromSeedString } from "@mirage/shared/util/tiny-utils";
import type { ComponentType, SVGAttributes } from "react";

const CUSTOM_ICON_PREFIX = "custom-icon-";

export function customIconToIconString(icon: AssistActionCustomIcon) {
  return `${CUSTOM_ICON_PREFIX}${icon.name}`;
}

export function parseCustomIconFromIconString(icon: string) {
  if (!icon.startsWith(CUSTOM_ICON_PREFIX)) {
    return null;
  }
  return icon.slice(CUSTOM_ICON_PREFIX.length);
}

export function getCustomIconForIconString(icon: string) {
  const name = parseCustomIconFromIconString(icon);
  if (!name) {
    return undefined;
  }
  return ALL_ASSIST_ACTION_CUSTOM_ICONS.find((icon) => icon.name === name);
}

export interface AssistActionCustomIcon {
  name: string;
  icon: ComponentType<SVGAttributes<SVGElement>>;
  color?: string;
}

/**
 * Custom icons that are available to users to create their own actions.
 */
export const USER_ASSIST_ACTION_CUSTOM_ICONS: AssistActionCustomIcon[] = [
  {
    name: "scribble",
    icon: ScribbleLine,
  },
  {
    name: "to-do-list",
    icon: ToDoListLine,
  },
  {
    name: "puzzle-piece",
    icon: PuzzlePieceLine,
  },
  {
    name: "lightbulb",
    icon: LightbulbLine,
  },
  {
    name: "compass",
    icon: CompassLine,
  },
  {
    name: "emoji-smile",
    icon: EmojiSmileLine,
  },
  {
    name: "location",
    icon: LocationLine,
  },
  {
    name: "briefcase",
    icon: BriefcaseLine,
  },
  {
    name: "target",
    icon: TargetLine,
  },
  {
    name: "task-queue",
    icon: TaskQueueLine,
  },
  {
    name: "metrics",
    icon: MetricsLine,
  },
  {
    name: "metal",
    icon: MetalLine,
  },
  {
    name: "fingerprint",
    icon: FingerprintLine,
  },
  {
    name: "bookmark",
    icon: BookmarkLine,
  },
  {
    name: "stamp",
    icon: StampLine,
  },
  {
    name: "unknown",
    icon: UnknownLine,
  },
];

export const CUSTOM_ICON_COLORS = [
  "var(--dig-color__identity__yellow)",
  "var(--dig-color__identity__green)",
  "var(--dig-color__identity__purple)",
  "var(--dig-color__identity__cyan)",
  "var(--dig-color__identity__magenta)",
  "var(--dig-color__identity__blue)",
  "var(--dig-color__identity__orange)",
];

export const PRESET_ASSIST_ACTION_CUSTOM_ICONS: AssistActionCustomIcon[] = [
  {
    name: "summarize",
    icon: SummarizeLine,
    color: "var(--dig-color__identity__yellow)",
  },
  {
    name: "rocket",
    icon: RocketLine,
    color: "var(--dig-color__identity__green)",
  },
  {
    name: "paper-doc",
    icon: PaperDocLine,
    color: "var(--dig-color__identity__purple)",
  },
  {
    name: "help",
    icon: HelpLine,
    color: "var(--dig-color__identity__cyan)",
  },
  {
    name: "heart",
    icon: HeartLine,
    color: "var(--dig-color__identity__magenta)",
  },
  {
    name: "conversation",
    icon: ConversationLine,
    color: "var(--dig-color__identity__blue)",
  },
  {
    name: "legal",
    icon: LegalLine,
    color: "var(--dig-color__identity__orange)",
  },
  {
    name: "code",
    icon: CodeLine,
    color: "var(--dig-color__identity__yellow)",
  },
  {
    name: "analytics",
    icon: AnalyticsLine,
    color: "var(--dig-color__identity__green)",
  },
  {
    name: "block-quote",
    icon: BlockQuoteLine,
    color: "var(--dig-color__identity__purple)",
  },
  {
    name: "link",
    icon: LinkLine,
    color: "var(--dig-color__identity__cyan)",
  },
  {
    name: "edit",
    icon: EditLine,
    color: "var(--dig-color__identity__magenta)",
  },
  {
    name: "comment-read",
    icon: CommentReadLine,
    color: "var(--dig-color__identity__blue)",
  },
];

export const ALL_ASSIST_ACTION_CUSTOM_ICONS: AssistActionCustomIcon[] = [
  ...PRESET_ASSIST_ACTION_CUSTOM_ICONS,
  ...USER_ASSIST_ACTION_CUSTOM_ICONS,
];

export function getAssistActionIconColor(
  icon: AssistActionCustomIcon | undefined,
  actionID: string,
) {
  if (icon?.color) {
    return icon.color;
  }
  // return random color (deterministic)
  const colorIndex = indexFromSeedString(actionID, CUSTOM_ICON_COLORS.length);
  return CUSTOM_ICON_COLORS[colorIndex];
}
